import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, input, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserIssueInterface } from 'voxr-shared';
import { AppService, IssueReportRequest, IssueReportResponse } from '../../../services/app.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-report-issue',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, MatDialogModule,
    MatTooltipModule, MatRippleModule, MatProgressBarModule, MatProgressSpinnerModule,
  ],
  templateUrl: './report-issue.component.html',
})
export class ReportIssueComponent implements OnInit {
  private app = inject(AppService);
  private route = inject(ActivatedRoute);

  softphone = input(false);
  debugProvider = input<(() => Promise<unknown>) | undefined>(undefined);

  submissionResult = input<((response: IssueReportResponse, debug: unknown) => void) | undefined>(undefined);

  dialog = inject(MatDialog)
  issue: Partial<UserIssueInterface> = {
    description: ''
  }
  dialogRef: any;
  downloadIssueId = signal<string | undefined>(undefined);
  downloadStatus = signal<'loading' | 'downloaded' | 'error'>('loading');
  downloadErrorMessage = signal('The debug file could not be downloaded.');

  ngOnInit() {
    const issueId = this.route.snapshot.paramMap.get('issueId') || undefined;
    this.downloadIssueId.set(issueId);
    if (issueId) void this.downloadDebugFile(issueId);
  }

  private async downloadDebugFile(issueId: string) {
    try {
      const response = await this.app.downloadUserIssueDebugFile(issueId);
      const blob = response.body;
      if (!blob) throw new Error('Empty debug file');

      const disposition = response.headers.get('Content-Disposition') || '';
      const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || `softphone-issue-${issueId}.json`;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      this.downloadStatus.set('downloaded');
    } catch (error) {
      const status = error instanceof HttpErrorResponse ? error.status : undefined;
      this.downloadErrorMessage.set(status === 401 || status === 403
        ? 'You do not have permission to download this debug file.'
        : status === 404
          ? 'This debug file is unavailable or no longer exists.'
          : 'The debug file could not be downloaded. Try again later.');
      this.downloadStatus.set('error');
    }
  }
  
  async openReportIssueDialog(template: any) {
    this.dialogRef = this.dialog.open(template);
  }
  
  loading = false;
  async submitIssue() {
    if (this.loading) return;
    const description = this.issue.description?.trim();
    if (this.softphone() && !description) return;

    try {
      this.loading = true;
      if (this.softphone()) this.issue.description = description;
      this.issue.url = window.location.href;
      this.issue.userAgent = navigator.userAgent;
      this.issue.clientTimestamp = new Date();
      const request: IssueReportRequest = { ...this.issue };
      if (this.softphone()) {
        request.category = 'softphone';
        try {
          const debug = await this.debugProvider()?.();
          request.debug = debug && typeof debug === 'object' && !Array.isArray(debug)
            ? debug
            : { schemaVersion: 1, capturedAt: new Date(), captureError: 'Debug capture failed' };
        } catch {
          request.debug = {
            schemaVersion: 1,
            capturedAt: new Date(),
            captureError: 'Debug capture failed',
          };
        }
      } else {
        request.category = 'general';
        request.errorLogs = this.app.getErrorLogs();
      }

      const response = await this.app.reportIssue(request);
      try { this.submissionResult()?.(response, request.debug); } catch { /* Cleanup cannot invalidate an acknowledged report. */ }
      request.debug = undefined;
      this.dialogRef.close();
      this.issue = { description: '' };
      this.app.message$.next({
        severity: 'success',
        summary: this.softphone() ? 'Soft phone report sent' : 'Issue reported',
        detail: this.softphone() ? `Report sent. Reference: ${response.issueId}` : 'Issue reported successfully.',
      });
    } catch {
      this.app.message$.next({
        severity: 'danger',
        summary: 'Report not sent',
        detail: 'Report could not be sent. Try again.',
      });
    } finally {
      this.loading = false;
    }
  }
}
